import { useTranslation } from 'react-i18next';
import { MaterialListRow } from '@/components/materials/MaterialListRow';
import { cn } from '@/lib/utils';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

const SortIcon = ({ field, sortBy, sortOrder }) => {
  if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
  return sortOrder === 'asc'
    ? <ArrowUp className="h-3 w-3 text-orange-500" />
    : <ArrowDown className="h-3 w-3 text-orange-500" />;
};

const SortHeader = ({ field, label, className, testId, sortBy, sortOrder, onSort }) => (
  <button
    type="button"
    onClick={() => onSort(field)}
    className={cn(
      'flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300 hover:text-orange-500 transition-colors text-left',
      className
    )}
    data-testid={testId}
  >
    <span className="truncate">{label}</span>
    <SortIcon field={field} sortBy={sortBy} sortOrder={sortOrder} />
  </button>
);

/**
 * Reusable material list view: sortable header row + compact text-only rows.
 * Used on HomePage materials section and on SearchPage.
 */
export const MaterialList = ({
  materials,
  optionalCols = [],
  sortBy,
  sortOrder,
  onSort,
  watchlist = [],
  onWatchlistToggle,
}) => {
  const { t } = useTranslation();
  const headerProps = { sortBy, sortOrder, onSort };

  return (
    <div data-testid="material-list-view" className="flex flex-col">
      {/* Sortable header row */}
      <div
        className="hidden sm:flex items-center gap-4 px-4 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 mb-1.5"
        data-testid="material-list-header"
      >
        <SortHeader
          {...headerProps}
          field="common_title"
          label={t('list.col.title', 'Bezeichnung')}
          className="flex-1 min-w-0"
          testId="sort-header-title"
        />
        {optionalCols.includes('common_description') && (
          <span className="hidden lg:flex flex-1 min-w-0 text-[10px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">
            {t('list.col.description', 'Beschreibung')}
          </span>
        )}
        {optionalCols.includes('manufacturer_part_number') && (
          <SortHeader
            {...headerProps}
            field="manufacturer_part_number"
            label={t('list.col.partNumber', 'Art.-Nr.')}
            className="hidden md:flex w-32 shrink-0"
            testId="sort-header-part-number"
          />
        )}
        {optionalCols.includes('material_index') && (
          <SortHeader
            {...headerProps}
            field="material_index"
            label={t('list.col.materialIndex', 'Mat.-Index')}
            className="hidden md:flex w-24 shrink-0"
            testId="sort-header-material-index"
          />
        )}
        <SortHeader
          {...headerProps}
          field="width"
          label={t('list.col.width', 'Breite')}
          className="hidden sm:flex w-24 shrink-0"
          testId="sort-header-width"
        />
        {optionalCols.includes('thickness') && (
          <SortHeader
            {...headerProps}
            field="thickness"
            label={t('list.col.thickness', 'Stärke')}
            className="hidden sm:flex w-24 shrink-0"
            testId="sort-header-thickness"
          />
        )}
        {optionalCols.includes('core_diameter') && (
          <SortHeader
            {...headerProps}
            field="core_diameter_optional"
            label={t('list.col.coreDiameter', 'Hülse')}
            className="hidden md:flex w-20 shrink-0"
            testId="sort-header-core-diameter"
          />
        )}
        {optionalCols.includes('material_grade') && (
          <SortHeader
            {...headerProps}
            field="material_grade_optional"
            label={t('list.col.grade', 'Grade')}
            className="hidden md:flex w-24 shrink-0"
            testId="sort-header-grade"
          />
        )}
        {optionalCols.includes('category') && (
          <SortHeader
            {...headerProps}
            field="category_name"
            label={t('list.col.category', 'Kategorie')}
            className="hidden lg:flex w-32 shrink-0"
            testId="sort-header-category"
          />
        )}
        {optionalCols.includes('is_special') && (
          <SortHeader
            {...headerProps}
            field="is_special"
            label={t('list.col.special', 'Spezial')}
            className="hidden md:flex w-20 shrink-0"
            testId="sort-header-special"
          />
        )}
        <span className="hidden md:flex w-24 shrink-0 text-[10px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">
          {t('list.col.skus', 'Positionen')}
        </span>
        {optionalCols.includes('number_of_rolls') && (
          <SortHeader
            {...headerProps}
            field="number_of_rolls"
            label={t('list.col.numberOfRolls', 'Rollen')}
            className="hidden md:flex w-20 shrink-0"
            testId="sort-header-rolls"
          />
        )}
        <span className="hidden lg:flex w-40 shrink-0 text-[10px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">
          {t('list.col.location', 'Standort')}
        </span>
        <SortHeader
          {...headerProps}
          field="quantity"
          label={t('list.col.availability', 'Bestand')}
          className="hidden md:flex w-28 shrink-0 justify-end"
          testId="sort-header-quantity"
        />
        {optionalCols.includes('created_at') && (
          <SortHeader
            {...headerProps}
            field="created_at"
            label={t('list.col.createdAt', 'Erstelldatum')}
            className="hidden md:flex w-28 shrink-0 justify-end"
            testId="sort-header-created"
          />
        )}
        <SortHeader
          {...headerProps}
          field="list_price_kEUR"
          label={t('list.col.price', 'Preis')}
          className="w-28 sm:w-32 shrink-0 justify-end"
          testId="sort-header-price"
        />
        <span className="w-8 shrink-0" aria-hidden="true" />
      </div>

      <div className="flex flex-col gap-1.5">
        {materials.map((material) => (
          <MaterialListRow
            key={material.material_id}
            material={material}
            onAddToWatchlist={onWatchlistToggle}
            isInWatchlist={watchlist.includes(material.material_id)}
            optionalColumns={optionalCols}
          />
        ))}
      </div>
    </div>
  );
};
